import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { Dashboard } from './components/Dashboard';
import { Projects } from './components/Projects';
import { Reports } from './components/Reports';
import { Alerts } from './components/Alerts';
import { Analytics } from './components/Analytics';
import { ProjectDetail } from './components/ProjectDetail';
import { ProjectSites } from './components/ProjectSites';
import { PilotUpload } from './components/PilotUpload';
import { AnnotatorUpload } from './components/AnnotatorUpload';
import { Login } from './components/Login';
import { ClientManagement } from './components/admin/ClientManagement';
import { UserManagement } from './components/UserManagement';
import { useAuthStore } from './store/auth';
import { logout as logoutApi } from './lib/auth-api';
import { useUrlRouter } from './hooks/useUrlRouter';

export default function App() {
  // Auth state lives in the zustand store; tab-level UI state stays local.
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const activeClientId = useAuthStore((s) => s.activeClientId);
  const isAuthenticated = Boolean(accessToken && user);

  // When the active tenant changes (e.g. super-admin uses the header tenant
  // switcher), invalidate every cached query so the dashboard / reports /
  // analytics / notifications tabs all refetch under the new X-Client-Id.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!activeClientId) return;
    queryClient.invalidateQueries();
  }, [activeClientId, queryClient]);

  // URL <-> section sync.  Direct URLs work: /projects, /admin/clients, /register, etc.
  const [activeSection, navigateSection] = useUrlRouter('dashboard');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedInspectionId, setSelectedInspectionId] = useState(null);

  // NOTE: deliberately no post-login URL normalization here.
  // useUrlRouter already initialized `activeSection` from the URL the user
  // landed on (e.g. /register, /admin/clients/new), and pushing the canonical
  // URL after login would happen *before* ClientManagement mounts and reads
  // the path — discarding the "register intent" and landing on the list view.
  // The URL stays as-is; ClientManagement.initialModeFromUrl() honours it
  // and switches to onboarding mode when the path matches /register* etc.

  if (status === 'bootstrapping') {
    return (
      <div className="dark h-screen w-screen flex items-center justify-center bg-background text-foreground">
        <div className="flex flex-col items-center gap-3 opacity-80">
          <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          <span className="text-sm text-muted-foreground">Restoring your session…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Login onLogin={() => {}} />;

  const handleLogout = async () => {
    await logoutApi();
  };

  const goSection = (section) => {
    navigateSection(section);
    setSelectedProject(null);
    setSelectedInspectionId(null);
  };

  const renderContent = () => {
    if (selectedInspectionId && selectedProject) {
      return (
        <ProjectDetail
          inspectionId={selectedInspectionId}
          onBack={() => setSelectedInspectionId(null)}
        />
      );
    }
    if (selectedProject) {
      return (
        <ProjectSites
          projectId={selectedProject.id}
          projectTitle={selectedProject.title}
          onBack={() => setSelectedProject(null)}
          onSiteClick={(inspectionId) => setSelectedInspectionId(inspectionId)}
        />
      );
    }

    // Pilot/Annotator upload screens are super-admin-only — block direct
    // URL access for client_admin / client_user / viewer.
    const superOnly = new Set(['pilot-upload', 'annotator-upload']);
    if (superOnly.has(activeSection) && !user?.is_super_admin) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground gap-2">
          <h2 className="text-xl font-semibold text-foreground">This area is restricted.</h2>
          <p className="text-sm">Pilot &amp; Annotator uploads are managed by Akin platform admins.</p>
        </div>
      );
    }

    switch (activeSection) {
      case 'dashboard':        return <Dashboard
                                  onProjectClick={(id, title) => setSelectedProject({ id, title })}
                                  onJumpToReports={() => goSection('reports')}
                                  onJumpToAnalytics={() => goSection('analytics')}
                                  onJumpToAlerts={() => goSection('alerts')}
                                />;
      case 'projects':         return <Projects
                                  onNavigateToAnalytics={() => goSection('analytics')}
                                  onProjectSelect={(p) => setSelectedProject(p)}
                                />;
      case 'reports':          return <Reports />;
      case 'alerts':           return <Alerts />;
      case 'analytics':        return <Analytics />;
      case 'pilot-upload':     return <PilotUpload />;
      case 'annotator-upload': return <AnnotatorUpload />;
      case 'users':            return <UserManagement />;
      case 'admin-clients':    return <ClientManagement />;
      default:                 return <Dashboard
                                  onProjectClick={(id, title) => setSelectedProject({ id, title })}
                                  onJumpToReports={() => goSection('reports')}
                                  onJumpToAnalytics={() => goSection('analytics')}
                                  onJumpToAlerts={() => goSection('alerts')}
                                />;
    }
  };

  return (
    <div className="dark h-screen bg-background text-foreground">
      <div className="flex h-full">
        <Sidebar
          activeSection={activeSection}
          onSectionChange={goSection}
          onProjectSelect={(project) => {
            navigateSection('projects');
            setSelectedInspectionId(null);
            setSelectedProject(project);
          }}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <main
          className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
            sidebarCollapsed ? 'ml-16' : 'ml-64'
          }`}
        >
          <Header
            activeSection={activeSection}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            onLogout={handleLogout}
          />

          <div className="flex-1 overflow-auto bg-background p-6">{renderContent()}</div>
        </main>
      </div>
    </div>
  );
}
